import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  BD_ADMIN_PERMISSION,
  BdAdminPermissionService,
  BdCompanyContextService,
  BdGrantsService,
  BdGraphApiUser,
  BdPermissionsUsersAdminService,
  BdShareablesService,
  BdUsersCacheService,
  revokeGrantConfirmMessage,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { BdConfirmDeleteService } from '@bindoc-projects/ui-lib/dialog/ui';
import {
  AccessGraph,
  AccessGraphEntry,
  AccessGraphFolderRef,
  AccessGraphShareTargetRef,
  AccessGraphShareableRef,
  AccessPath,
  AccessPathSegment,
  accessOriginOf,
  accessPathText,
  activePermissionsOf,
  BdSharePermission,
  BdSharePermissionHelper,
  describeAccessPath,
  hasContextFreeAccess,
  isContextFree,
  isInactiveInContext,
  isManagedElsewhere,
  revocationTargetOf,
  ShareableReference,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { NgTemplateOutlet } from '@angular/common';
import { Button, ButtonDirective, ButtonIcon } from 'primeng/button';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableModule } from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import { BdWorkspaceLinkService } from '../workspace-link/workspace-link.service';
import {
  catchError,
  forkJoin,
  Observable,
  of,
  take,
  tap,
  throwError,
} from 'rxjs';

/** One derivation of an entry's access, rendered as a sentence. */
export interface PermissionPath {
  /** The sentence, split so the template can emphasise the named parts. */
  segments: AccessPathSegment[];
  /** The same sentence as plain text, for tooltips and assertions. */
  text: string;
  /** The access follows the target into every company context. */
  contextFree: boolean;
  /** `false` only when a company context was requested and this path is outside it. */
  activeInContext: boolean;
  /**
   * Revoking this path would act on a containing folder rather than on the
   * shareable itself — or is impossible (folder ownership). Either way the
   * dialog cannot offer it here.
   */
  managedElsewhere: boolean;
  /**
   * `'folder'` when the path runs through a containing folder, `'direct'` when
   * it touches the shareable itself. Combined with
   * {@link managedElsewhere} this is what separates "inherited from a folder"
   * from "owns this very item".
   */
  origin: 'folder' | 'direct';
  /** What a revoke call would have to reference. `null` when not revocable. */
  revocationTarget: AccessGraphShareableRef | null;
}

/** Why a row has access. Drives one glyph in the "Herkunft" column. */
export type AccessOriginKind = 'owner' | 'folder' | 'shared';

/** One "Herkunft" glyph, with the wording that explains it. */
export interface AccessOrigin {
  kind: AccessOriginKind;
  icon: string;
  /** Short name, used as both `aria-label` and `title`. */
  label: string;
  tooltip: string;
  testId: string;
}

/**
 * The three origins, in the order they render. Ordered from "least removable"
 * to "most": ownership cannot be revoked at all, a folder grant only on the
 * folder, a direct grant right here.
 */
const ACCESS_ORIGINS: Record<AccessOriginKind, AccessOrigin> = {
  owner: {
    kind: 'owner',
    icon: 'pi pi-folder',
    label: 'Eigentümer',
    tooltip:
      'Eigentümer dieses Elements. Dieser Zugriff lässt sich nicht entziehen.',
    testId: 'permissions-table-origin-owner',
  },
  folder: {
    kind: 'folder',
    icon: 'pi pi-sitemap',
    label: 'Im Ordner verwaltet',
    tooltip:
      'Der Zugriff wird auf einem übergeordneten Ordner verwaltet und kann nur dort entfernt werden.',
    testId: 'permissions-table-origin-folder',
  },
  shared: {
    kind: 'shared',
    icon: 'pi pi-share-alt',
    label: 'Direkt freigegeben',
    tooltip: 'Direkt auf diesem Element freigegeben.',
    testId: 'permissions-table-origin-shared',
  },
};

/**
 * One access anchor as the table renders it. An anchor is a user, group or
 * company — a grant to a group is one row for the group, with its members
 * nested under {@link users}, rather than one row per member as the removed
 * `allowed-permissions-per-share-target` endpoint produced.
 */
export interface PermissionEntry {
  shareTarget: AccessGraphShareTargetRef;
  /**
   * The permissions that apply in the selected company context. Derived from
   * the active paths, not copied from `entry.permissions` — that field is the
   * union over *all* paths and would light up a row whose every path is
   * inactive here.
   */
  allowedShareablePermissions: BdSharePermission[];
  /** The union across all paths, regardless of context, as the backend reports it. */
  allPermissions: BdSharePermission[];
  paths: PermissionPath[];
  /** Effective members, present only for a group or company anchor. */
  users: PermissionEntry[];
  /** No path applies in the selected company context. */
  inactiveInContext: boolean;
  /** At least one path is context-free. */
  contextFree: boolean;
  /**
   * Every distinct reason this row has access, in render order — one glyph per
   * entry in the "Herkunft" column. A row legitimately holds more than one
   * (a direct grant *and* an inherited one), which is why this is a list and
   * not a single value.
   */
  origins: AccessOrigin[];
}

@Component({
  selector: 'bd-permissions-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (errorState() !== 'none') {
      <div class="flex items-start gap-3" data-test="permissions-table-error">
        <i
          class="text-2xl text-bd-warning"
          [class.pi]="true"
          [class.pi-lock]="errorState() === 'forbidden'"
          [class.pi-exclamation-triangle]="errorState() === 'error'"
        ></i>
        <p class="m-0">
          @if (errorState() === 'forbidden') {
            Sie haben keine Berechtigung, die Zugriffsübersicht dieser Ressource
            anzuzeigen.
          } @else {
            Die Zugriffsübersicht konnte nicht geladen werden. Bitte versuchen
            Sie es später erneut.
          }
        </p>
      </div>
    } @else {
      <p-table
        [value]="permissions()"
        [paginator]="permissions().length > 10"
        [rows]="10"
        size="small"
        stripedRows
        dataKey="shareTarget.id"
      >
        <ng-template #header>
          <tr>
            <th class="w-8"></th>
            <th>Freigabe für</th>
            <th class="w-24">Herkunft</th>
            <th>Zugriff über</th>
            <th class="w-32"></th>
          </tr>
        </ng-template>
        <ng-template #body let-entry let-expanded="expanded">
          <tr [class.opacity-60]="entry.inactiveInContext">
            <td>
              @if (entry.users.length > 0) {
                <button
                  pButton
                  text
                  rounded
                  size="small"
                  severity="secondary"
                  type="button"
                  [pRowToggler]="entry"
                  [attr.aria-label]="
                    (expanded
                      ? 'Mitglieder ausblenden von '
                      : 'Mitglieder anzeigen von ') + displayName(entry)
                  "
                  [attr.title]="
                    expanded ? 'Mitglieder ausblenden' : 'Mitglieder anzeigen'
                  "
                  data-test="permissions-table-expand"
                >
                  <i
                    class="pi"
                    [class.pi-chevron-down]="expanded"
                    [class.pi-chevron-right]="!expanded"
                    pButtonIcon
                  ></i>
                </button>
              }
            </td>
            <ng-container
              [ngTemplateOutlet]="entryCells"
              [ngTemplateOutletContext]="{ $implicit: entry, nested: false }"
            ></ng-container>
          </tr>
        </ng-template>
        <!--
          PrimeNG 21 renamed this template: 'rowexpansion' silently matches
          nothing, and the row then toggles with no content to show.

          The outlet drops straight into the tbody with no wrapping <tr>, so
          members render as real sibling rows reusing the anchor's cells — the
          columns line up because it is literally the same cell template.
        -->
        <ng-template #expandedrow let-entry>
          @for (member of entry.users; track member.shareTarget.id) {
            <tr
              class="bg-[var(--p-bd-surface-subtle)]"
              [class.opacity-60]="member.inactiveInContext"
              data-test="permissions-table-member"
            >
              <td></td>
              <ng-container
                [ngTemplateOutlet]="entryCells"
                [ngTemplateOutletContext]="{ $implicit: member, nested: true }"
              ></ng-container>
            </tr>
          }
        </ng-template>
      </p-table>
      <p-confirmdialog />

      <!--
      The four content cells of a row, shared by an access anchor and by each
      expanded member beneath it. Sharing one template is what keeps the two row
      kinds structurally identical; "nested" only indents the name.
    -->
      <ng-template #entryCells let-entry let-nested="nested">
        <td>
          <div class="flex flex-row items-center gap-2" [class.pl-6]="nested">
            <i [class]="iconFor(entry)"></i>
            <div class="flex flex-col">
              <!--
              The email disambiguates two people with the same display name.
              Only an ADMIN:USER holder may see it, and only for a user target,
              so the tooltip is disabled whenever there is nothing to show.
            -->
              <span
                [pTooltip]="emailFor(entry)"
                [tooltipDisabled]="!emailFor(entry)"
                tooltipPosition="top"
                data-test="permissions-table-name"
                >{{ displayName(entry) }}</span
              >
              @if (entry.users.length > 0) {
                <span
                  class="text-xs text-muted-color"
                  pTooltip="Die Berechtigung eines Mitglieds ist nicht zwangsläufig die der Gruppe."
                  tooltipPosition="top"
                >
                  {{ entry.users.length }}
                  {{ entry.users.length === 1 ? 'Mitglied' : 'Mitglieder' }}
                </span>
              }
            </div>
            @if (entry.contextFree) {
              <p-tag
                severity="secondary"
                value="Kundenübergreifend"
                pTooltip="Diese Freigabe gilt in jedem Kundenkontext, auch nach einem Wechsel."
                tooltipPosition="top"
                data-test="permissions-table-context-free"
              />
            }
            @if (entry.inactiveInContext) {
              <p-tag
                severity="secondary"
                value="Anderer Kunde"
                pTooltip="Diese Freigabe ist im aktuell gewählten Kundenkontext nicht wirksam."
                tooltipPosition="top"
                data-test="permissions-table-inactive"
              />
            }
          </div>
        </td>
        <!--
        Where the access comes from, as a glyph instead of the "(im Ordner
        verwaltet)" suffix the path sentences used to carry. Three states, since
        the two non-revocable ones are not the same thing: owning the item
        itself (folder), inheriting down the tree from a containing folder
        (sitemap), and a grant on the item itself (share). Aggregated over the
        row's paths on purpose — a row legitimately holds several kinds, and
        then several glyphs show. Which folder, and who granted it, stays in
        the sentence next door.
      -->
        <td>
          <div class="flex flex-row items-center gap-1">
            @for (origin of entry.origins; track origin.kind) {
              @if (!$first) {
                <!--
                Two glyphs side by side read as a single compound state; the
                ampersand says they are two reasons, both of which apply.
                Decorative for screen readers — each glyph already carries its
                own label, so a spoken "und" between them adds nothing.
              -->
                <span class="text-xs text-muted-color" aria-hidden="true"
                  >&amp;</span
                >
              }
              <i
                [class]="origin.icon + ' text-muted-color'"
                [pTooltip]="origin.tooltip"
                tooltipPosition="top"
                [attr.aria-label]="origin.label"
                [attr.title]="origin.label"
                [attr.data-test]="origin.testId"
              ></i>
            }
          </div>
        </td>
        <!--
        No separate permission column: every path sentence below already opens
        with the permissions it confers ("Anzeigen, Bearbeiten — …"), so an
        avatar row beside it only restated their union.
      -->
        <td>
          <ul class="m-0 flex list-none flex-col gap-1 p-0">
            @for (path of entry.paths; track $index) {
              <li
                class="text-xs"
                [class.opacity-60]="!path.activeInContext"
                data-test="permissions-table-path"
              >
                @for (segment of path.segments; track $index) {
                  @if (segment.folder && !showFolderLinks()) {
                    <!--
                    No workspace to jump to (administration app, or an app
                    without a meta-app origin — see BD_WORKSPACE_LOCATION): the folder is
                    still the emphasised part of the sentence, just not a link.
                    Rendering the anchor anyway would promise a destination the
                    host cannot route to.
                  -->
                    <span class="font-medium">{{ segment.text }}</span>
                  } @else if (segment.folder) {
                    <!--
                    A named folder is the one part of the sentence with a
                    destination, so the jump sits here rather than on the row
                    glyph, which aggregates paths and could not pick one folder.

                    Category 06: in-app navigation, icon-only, so the glyph is
                    the label and carries aria-label + title. Not an underlined
                    text link — that is category 05 and promises "leaves the
                    app". An <a> rather than a <button> so the href is a real
                    deep link and the browser's open-in-new-tab works.
                  -->
                    <!--
                    Name and glyph are one anchor, not a plain label beside an
                    icon button: hovering anywhere on the pair lights both up,
                    which is what makes the glyph read as "open *this* folder"
                    rather than as a loose button next to some text. It is also
                    one tab stop and one accessible name.

                    Resting state is deliberately plain — inherited colour, no
                    underline — so it does not read as a category 05 external
                    link. The affordance appears on hover.
                  -->
                    <a
                      class="group inline-flex items-baseline gap-1 text-inherit no-underline"
                      [href]="folderLink(segment.folder)"
                      [attr.target]="externalFolderLinks ? '_blank' : null"
                      [attr.rel]="
                        externalFolderLinks ? 'noopener noreferrer' : null
                      "
                      [attr.aria-label]="
                        'Ordner ' + segment.text + ' im Workspace öffnen'
                      "
                      title="Ordner im Workspace öffnen"
                      pTooltip="Ordner im Workspace öffnen"
                      tooltipPosition="top"
                      (click)="openFolder($event, segment.folder)"
                      data-test="permissions-table-folder-link"
                    >
                      <span class="font-medium group-hover:underline">{{
                        segment.text
                      }}</span>
                      <i
                        class="pi pi-external-link text-muted-color text-xs! group-hover:text-surface-700"
                      ></i>
                    </a>
                  } @else if (segment.emphasis) {
                    <span class="font-medium">{{ segment.text }}</span>
                  } @else {
                    <span>{{ segment.text }}</span>
                  }
                }
              </li>
            }
          </ul>
        </td>
        <td>
          <div class="flex flex-row items-center justify-end gap-2">
            @if (canRevokeEntry(entry, nested)) {
              <!--
              Category 04 (destructive): a red glyph, never a red fill. It still
              confirms before revoking — the dialog's danger button repeats the
              colour instead of being the only place it appears.
            -->
              <p-button
                icon="pi pi-trash"
                text
                rounded
                size="small"
                severity="danger"
                ariaLabel="Freigabe entfernen"
                pTooltip="Freigabe entfernen"
                tooltipPosition="top"
                (onClick)="revoke(entry)"
                [pt]="{
                  root: {
                    title: 'Freigabe entfernen',
                    'data-test': 'permissions-table-revoke',
                  },
                }"
              />
            }
            @if (canViewDetails(entry)) {
              <!--
              Category 02 (Action): filled tonal grey, label only. Not "text"
              and not glyph-decorated — and never without a severity, which
              would render orange primary and compete with the view's real CTA.
            -->
              <p-button
                label="Details"
                severity="secondary"
                size="small"
                (onClick)="
                  detailRequested.emit({
                    entry: entry,
                    resolvedName: displayName(entry),
                  })
                "
                [pt]="{ root: { 'data-test': 'permissions-table-details' } }"
              />
            }
          </div>
        </td>
      </ng-template>
    }
  `,
  imports: [
    NgTemplateOutlet,
    TableModule,
    Button,
    ButtonDirective,
    ButtonIcon,
    Tag,
    Tooltip,
    ConfirmDialog,
  ],
  providers: [ConfirmationService, BdConfirmDeleteService],
})
export class BdPermissionsTableComponent {
  public readonly shareableType = input.required<ShareableType>();
  public readonly shareableId = input.required<string>();
  /** Named in the revoke confirmation dialog. */
  public readonly shareableName = input<string>('');
  /**
   * Whether a named folder in a path sentence renders as a deep link into the
   * workspace. Only apps that actually route `/home/content` may leave this on
   * — the administration app has no workspace, so there the folder name stays
   * plain text instead of an anchor that navigates nowhere.
   */
  public readonly folderLinks = input<boolean>(true);
  public readonly detailRequested = output<{
    entry: PermissionEntry;
    resolvedName: string;
  }>();
  /**
   * The rows, every time the graph (re)loads — including after a revoke, which
   * this component performs on its own. A host that derives its own state from
   * the graph must listen here rather than fetch it a second time, or its copy
   * goes stale the moment a row is revoked.
   */
  public readonly permissionsLoaded = output<PermissionEntry[]>();

  /**
   * {@link folderLinks}, narrowed to apps that can reach a workspace at all —
   * the standalone Klia app links into the meta-app's instead, or hides the
   * links without a configured origin (`BD_WORKSPACE_LOCATION`).
   */
  public readonly showFolderLinks = computed(
    () => this.folderLinks() && this._workspaceLinks.available,
  );
  /** Folder links leave this app, so they open in a new tab. */
  public get externalFolderLinks(): boolean {
    return this._workspaceLinks.external;
  }

  public readonly permissions = signal<PermissionEntry[]>([]);
  /**
   * `'forbidden'` when the access graph returned 403 (the user lacks UPDATE),
   * `'error'` for any other failure, `'none'` otherwise. Previously the call
   * had no error handler, so a failure left a silently empty table (BDA-5507).
   */
  public readonly errorState = signal<'none' | 'forbidden' | 'error'>('none');

  /** Current user holds at least one admin permission. */
  private readonly _hasAdminPermission = signal(false);
  /** Current user has UPDATE (or higher) permission on the shareable itself. */
  private readonly _hasUpdatePermission = signal(false);
  /** Local account id of the signed-in user, used to spot their own grant. */
  private readonly _currentUserId = signal<string | null>(null);
  /**
   * Current user holds `ADMIN:USER` and may therefore see user emails. Checked
   * on its own rather than via {@link _hasAdminPermission}: an admin of
   * shareables has no business seeing addresses. `ADMIN:GOD` needs no special
   * case — the backend expands it into the four sub-permissions.
   */
  private readonly _canSeeUserEmails = signal(false);
  /** Resolved user id → email, filled only while {@link _canSeeUserEmails}. */
  private readonly _userEmails = signal<Record<string, string>>({});

  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _adminPermissionService = inject(BdAdminPermissionService);
  private readonly _authService = inject(BdAuthenticationService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _grantsService = inject(BdGrantsService);
  private readonly _usersAdminService = inject(BdPermissionsUsersAdminService);
  private readonly _usersCache = inject(BdUsersCacheService);
  private readonly _confirmDeleteService = inject(BdConfirmDeleteService);
  private readonly _workspaceLinks = inject(BdWorkspaceLinkService);
  /**
   * The dialog this table sits in, when it sits in one. Optional because the
   * component is also rendered directly (Storybook, and any future inline use)
   * — there is no ref to close then, and `inject` would throw.
   */
  private readonly _dialogRef = inject(DynamicDialogRef, { optional: true });

  constructor() {
    this._authService.accountInfo$
      .pipe(take(1))
      .subscribe((account) =>
        this._currentUserId.set(account?.localAccountId ?? null),
      );

    effect(() => {
      const type = this.shareableType();
      const id = this.shareableId();
      this._loadPermissions(type, id);
      this._resolveDetailAccess(type, id);
    });
  }

  /**
   * Whether the current user may open the detail dialog for a given row. Admins
   * may inspect every grant; a non-admin with UPDATE permission on the
   * shareable may only inspect their own grant (the row where they are the
   * share target). Anyone else gets no button.
   */
  public canViewDetails(entry: PermissionEntry): boolean {
    if (this._hasAdminPermission()) {
      return true;
    }
    return (
      this._hasUpdatePermission() &&
      entry.shareTarget.type === 'user' &&
      entry.shareTarget.id === this._currentUserId()
    );
  }

  /**
   * Whether a revoke button may render for this row (BDA-5971). The predicate
   * is per *entry* rather than per path because {@link revoke} is: it removes
   * the grantee's grant on this shareable, whichever path surfaced it. Asking
   * per path rendered one identical button per revocable path.
   *
   * A row is revocable only when some path names a grant on the dialog's own
   * shareable — never on an expanded group member (`nested`, where the button
   * would revoke the *group's* grant while sitting next to a member) and never
   * on a path {@link PermissionPath.managedElsewhere managed elsewhere}, which
   * needs DELETE on a containing folder instead of on this shareable.
   */
  public canRevokeEntry(entry: PermissionEntry, nested: boolean): boolean {
    return (
      !nested &&
      entry.paths.some(
        (path) => !path.managedElsewhere && path.revocationTarget !== null,
      ) &&
      (this._hasAdminPermission() || this._hasUpdatePermission())
    );
  }

  /**
   * The email to show for a row, or `''` when there is none to show — the
   * caller lacks `ADMIN:USER`, the target is not a user, or the lookup failed.
   */
  public emailFor(entry: PermissionEntry): string {
    if (entry.shareTarget.type !== 'user') {
      return '';
    }
    return this._userEmails()[entry.shareTarget.id] ?? '';
  }

  /**
   * Revokes the entry's grant on this shareable. `managedElsewhere === false`
   * guarantees the revocable path's target *is* this shareable (never a
   * containing folder), so the call always references the component's own
   * `shareableType`/`shareableId` rather than the path's
   * {@link PermissionPath.revocationTarget}, which is typed as a wire ref and
   * would need an unsound cast to become a {@link ShareableType}.
   */
  public revoke(entry: PermissionEntry): void {
    const grantee: ShareableReference = {
      id: entry.shareTarget.id,
      type: entry.shareTarget.type,
      name: this.displayName(entry),
    };
    this._confirmDeleteService
      .confirm({
        customHeader: 'Freigabe entfernen?',
        customMessage: revokeGrantConfirmMessage(
          this.shareableName() || this.shareableId(),
          grantee,
        ),
        customAcceptLabel: 'Ja, Freigabe entfernen',
      })
      .subscribe(() => {
        this._grantsService
          .revoke(
            { id: this.shareableId(), type: this.shareableType() },
            grantee,
          )
          .pipe(take(1))
          .subscribe(() =>
            this._loadPermissions(this.shareableType(), this.shareableId()),
          );
      });
  }

  /**
   * The label for an anchor. Every name is resolved server-side (concept 5), so
   * no id is looked up here — a group additionally carries its owning company,
   * which is what the former client-side `groupDisplayName` lookup existed to
   * produce. A `null` name means the target no longer resolves; the id is shown
   * rather than the row hidden, because access via a deleted target is worth
   * surfacing.
   */
  public displayName(entry: PermissionEntry): string {
    const name = entry.shareTarget.name?.trim() || entry.shareTarget.id;
    const company = entry.shareTarget.company?.name?.trim();
    if (entry.shareTarget.type === 'group' && company) {
      return `${company} / ${name}`;
    }
    return name;
  }

  /**
   * The deep link that opens a folder in the workspace. Folders have no route
   * of their own — the workspace is one component whose folder is internal
   * state — so the link is `/home/content?folderId=…`, the same receiver-side
   * resolution the existing "Im Ordner öffnen" items use for a shareable id.
   * Deliberately without `tab`: the folder may live in a company group, and a
   * tab would pin it to "Eigene Ressourcen" instead of letting the workspace
   * resolve its scope. Rendered as a real `href` so the browser's own
   * open-in-new-tab works.
   */
  public folderLink(folder: AccessGraphFolderRef): string {
    return this._workspaceLinks.href({ folderId: folder.id });
  }

  /**
   * Navigates to the folder, closing the dialog first — the workspace renders
   * underneath it, so leaving the dialog open would hide where the user landed.
   *
   * Modified clicks are left to the browser: the anchor's `href` is the same
   * URL, so ctrl/cmd/shift-click opens the folder in a new tab or window and
   * the current dialog stays as it was.
   */
  public openFolder(event: Event, folder: AccessGraphFolderRef): void {
    // An external workspace link opens in a new tab through the anchor itself
    // (`target="_blank"`); this app and its dialog stay where they are.
    if (this.externalFolderLinks) {
      return;
    }
    if (event instanceof MouseEvent && this._isModifiedClick(event)) {
      return;
    }
    event.preventDefault();
    this._dialogRef?.close();
    this._workspaceLinks.open({ folderId: folder.id });
  }

  private _isModifiedClick(event: MouseEvent): boolean {
    return event.ctrlKey || event.metaKey || event.shiftKey || event.button > 0;
  }

  public iconFor(entry: PermissionEntry): string {
    switch (entry.shareTarget.type) {
      case 'user':
        return 'pi pi-user';
      case 'group':
        return 'pi pi-users';
      case 'company':
        return 'pi pi-building';
      default:
        return 'pi pi-question';
    }
  }

  private _loadPermissions(type: ShareableType, id: string): void {
    this.errorState.set('none');
    const companyId = this._companyContext.currentCompanyId;
    this._fetchGraph(type, id, companyId).subscribe({
      next: (graph) => {
        const entries = graph.entries.map((entry) =>
          this._toEntry(entry, graph.shareable),
        );
        this.permissions.set(entries);
        this.permissionsLoaded.emit(entries);
        this._resolveUserEmails();
      },
      error: (err: unknown) => {
        this.permissions.set([]);
        this.permissionsLoaded.emit([]);
        this.errorState.set(
          err instanceof HttpErrorResponse && err.status === 403
            ? 'forbidden'
            : 'error',
        );
      },
    });
  }

  /**
   * The graph for the selected company context, falling back to a context-free
   * request on 403.
   *
   * The endpoint answers 403 both when the caller lacks UPDATE on the shareable
   * and when they are not a member of the company passed as `companyId` — and
   * the two are indistinguishable. The selected company is restored from local
   * storage and can be stale, so a single retry without the parameter avoids
   * telling a user who *does* have UPDATE that they have no permission.
   */
  private _fetchGraph(
    type: ShareableType,
    id: string,
    companyId: string | undefined,
  ): Observable<AccessGraph> {
    const request = this._resourcesService.accessGraph(type, id, {
      companyId,
      expandUsers: true,
    });
    if (!companyId) {
      return request;
    }
    return request.pipe(
      catchError((err: unknown) =>
        err instanceof HttpErrorResponse && err.status === 403
          ? this._resourcesService.accessGraph(type, id, { expandUsers: true })
          : throwError(() => err),
      ),
    );
  }

  private _resolveDetailAccess(type: ShareableType, id: string): void {
    forkJoin({
      adminPermissions: this._adminPermissionService
        .getPermissions()
        .pipe(catchError(() => of([]))),
      myPermission: this._resourcesService
        .myPermission(type, id)
        .pipe(catchError(() => of(undefined))),
    }).subscribe(({ adminPermissions, myPermission }) => {
      this._hasAdminPermission.set(adminPermissions.length > 0);
      this._canSeeUserEmails.set(
        adminPermissions.includes(BD_ADMIN_PERMISSION.USER),
      );
      this._hasUpdatePermission.set(
        !!myPermission &&
          BdSharePermissionHelper.includes(
            myPermission.highestPermission,
            'UPDATE',
          ),
      );
      // The graph and the permission lookup race; whichever lands second
      // resolves the emails, and the call is a no-op until both are in.
      this._resolveUserEmails();
    });
  }

  /**
   * Fills {@link _userEmails} for every user the table names — anchors and
   * expanded members alike, deduped, because the same person legitimately
   * appears in both places (a direct grant *and* a group membership).
   *
   * Only ids not already resolved are fetched, and each lookup is tolerant of
   * failure: the access graph deliberately keeps rows for deleted targets, and
   * looking one of those up 404s. A missing email means no tooltip, never an
   * error state for the whole table.
   */
  private _resolveUserEmails(): void {
    if (!this._canSeeUserEmails()) {
      return;
    }
    const known = this._userEmails();
    const missing = this._userIdsOf(this.permissions()).filter(
      (id) => !(id in known),
    );
    // forkJoin([]) completes without ever emitting, so guard the empty case.
    if (missing.length === 0) {
      return;
    }
    forkJoin(missing.map((id) => this._fetchUser(id)))
      .pipe(take(1))
      .subscribe((users) => {
        const resolved: Record<string, string> = { ...this._userEmails() };
        missing.forEach((id, index) => {
          resolved[id] = this._emailOf(users[index]);
        });
        this._userEmails.set(resolved);
      });
  }

  /** Distinct ids of every user target in the table, anchors and members. */
  private _userIdsOf(entries: PermissionEntry[]): string[] {
    const ids = new Set<string>();
    const collect = (entry: PermissionEntry): void => {
      if (entry.shareTarget.type === 'user') {
        ids.add(entry.shareTarget.id);
      }
      entry.users.forEach(collect);
    };
    entries.forEach(collect);
    return [...ids];
  }

  private _fetchUser(id: string): Observable<BdGraphApiUser | null> {
    const cached = this._usersCache.get(id);
    if (cached) {
      return of(cached);
    }
    return this._usersAdminService.getById(id).pipe(
      tap((user) => {
        if (user) {
          this._usersCache.set(user);
        }
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * `mail` is what the admin app shows for a user (search list and detail
   * page), so the tooltip matches what an admin sees elsewhere; `loginEmail`
   * only stands in when it is absent.
   */
  private _emailOf(user: BdGraphApiUser | null): string {
    return user?.mail?.trim() || user?.loginEmail?.trim() || '';
  }

  private _toEntry(
    raw: AccessGraphEntry,
    shareable: AccessGraphShareableRef,
  ): PermissionEntry {
    const paths = raw.paths.map((path) =>
      this._toPath(path, raw.shareTarget, shareable),
    );
    return {
      shareTarget: raw.shareTarget,
      allowedShareablePermissions: activePermissionsOf(raw),
      allPermissions: raw.permissions,
      paths,
      users: (raw.users ?? []).map((user) => this._toEntry(user, shareable)),
      inactiveInContext: isInactiveInContext(raw),
      contextFree: hasContextFreeAccess(raw),
      origins: this._originsOf(paths),
    };
  }

  /**
   * The distinct origins behind a row's paths, deduped and in render order.
   *
   * The three kinds partition the paths: a revocable grant sits right here
   * ('shared'), and a non-revocable one either reaches the shareable through a
   * containing folder ('folder') or does not ('owner' — in practice the anchor
   * owns the shareable itself). Splitting on {@link PermissionPath.origin} is
   * what keeps an owned folder from claiming to be "im Ordner verwaltet" while
   * naming no folder at all.
   */
  private _originsOf(paths: PermissionPath[]): AccessOrigin[] {
    const kinds = new Set<AccessOriginKind>(
      paths.map((path) => {
        if (!path.managedElsewhere) {
          return 'shared';
        }
        return path.origin === 'folder' ? 'folder' : 'owner';
      }),
    );
    // Iterate the constant, not the set, so the order is the declared one
    // rather than the order the backend happened to list the paths in.
    return (Object.keys(ACCESS_ORIGINS) as AccessOriginKind[])
      .filter((kind) => kinds.has(kind))
      .map((kind) => ACCESS_ORIGINS[kind]);
  }

  private _toPath(
    path: AccessPath,
    anchor: AccessGraphShareTargetRef,
    shareable: AccessGraphShareableRef,
  ): PermissionPath {
    const segments = describeAccessPath(path, anchor);
    return {
      segments,
      text: accessPathText(segments),
      contextFree: isContextFree(path),
      activeInContext: path.activeInContext !== false,
      managedElsewhere: isManagedElsewhere(path, shareable),
      origin: accessOriginOf(path),
      revocationTarget: revocationTargetOf(path),
    };
  }
}
